'use client';
import React, { useState, useEffect } from 'react';
import { Sparkles, FileText, Globe2, Users, CheckCircle2, ArrowRight, ChevronLeft, Eye } from 'lucide-react';
import type { WizardData } from './VisaWizard';

interface Props {
  data: WizardData;
  updateData: (d: Partial<WizardData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const generationSteps = [
  { id: 'gen-analysis', label: 'Analyzing your profile & documents', duration: 1200 },
  { id: 'gen-cover', label: 'Writing cover letter (embassy-specific)', duration: 2400 },
  { id: 'gen-itinerary', label: 'Building detailed travel itinerary', duration: 1800 },
  { id: 'gen-sponsor', label: 'Generating sponsor letter template', duration: 1400 },
  { id: 'gen-bundle', label: 'Bundling complete PDF package', duration: 1600 },
];

const coverLetterPreview = `Dear Visa Officer,

I, Layla Mohammed Al-Rashidi (Passport No. UAE-AB1234567), am writing to respectfully apply for a Schengen Tourist Visa (Type C) to the Federal Republic of Germany.

The purpose of my visit is tourism and cultural exploration. I plan to visit Berlin, Munich, and Hamburg from June 12, 2026 to July 10, 2026 — a total of 28 days. I have confirmed accommodation at Hotel Berliner Hof, Berlin, and hold a valid return flight booking (Emirates EK046, departing July 10, 2026).

I am a Senior Marketing Manager at Al-Rashidi Trading LLC, Dubai, UAE, with a monthly salary of AED 22,000. My employer has granted approved leave for this period, as evidenced by the attached employment letter. I have sufficient financial means to fund this trip independently, as demonstrated by my bank statement showing a balance of AED 63,400 (approx. €15,800).

I am a law-abiding resident of the UAE with strong ties to my home country, including my employment, family, and property. I intend to return to the UAE promptly at the conclusion of my approved stay.

I respectfully request that you consider my application favorably.

Yours sincerely,
Layla Mohammed Al-Rashidi`;

export default function Step7Generation({ data, onNext, onBack }: Props) {
  const [completedSteps, setCompletedSteps] = useState<number>(0);
  const [done, setDone] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    // TODO: Call /api/documents/generate with { applicationId, language, visaType, country }
    let elapsed = 0;
    generationSteps.forEach((step, idx) => {
      elapsed += step.duration;
      setTimeout(() => {
        setCompletedSteps(idx + 1);
        if (idx === generationSteps.length - 1) {
          setTimeout(() => setDone(true), 400);
        }
      }, elapsed);
    });
  }, []);

  const totalDuration = generationSteps.reduce((a, s) => a + s.duration, 0);
  const elapsedPct = Math.min((completedSteps / generationSteps.length) * 100, 100);

  return (
    <div className="wizard-card space-y-6 animate-slide-up">
      {/* Header */}
      <div className="space-y-1">
        <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest">Step 7 of 9</p>
        <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">
          {done ? 'Your package is ready!' : 'AI is generating your package...'}
        </h2>
        <p className="text-sm text-gray-500">
          {done
            ? `Complete application package for ${data.destinationFlag} ${data.destinationCountry}`
            : 'Writing your documents, building your itinerary, and bundling everything into a PDF.'
          }
        </p>
      </div>

      {/* Progress bar */}
      {!done && (
        <div className="space-y-2">
          <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-600 to-sky-400 rounded-full transition-all duration-700 ease-out"
              style={{ width: `${elapsedPct}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 text-right tabular-nums">{Math.round(elapsedPct)}% complete</p>
        </div>
      )}

      {/* Generation steps */}
      <div className="space-y-2">
        {generationSteps.map((step, idx) => {
          const isComplete = idx < completedSteps;
          const isActive = idx === completedSteps;
          return (
            <div
              key={step.id}
              className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-300 ${
                isComplete ? 'bg-green-50 border border-green-100' : isActive ?'bg-blue-50 border border-blue-100': 'bg-gray-50 border border-gray-100 opacity-50'
              }`}
            >
              {isComplete ? (
                <CheckCircle2 size={16} className="text-green-500 shrink-0" />
              ) : isActive ? (
                <div className="w-4 h-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin shrink-0" />
              ) : (
                <div className="w-4 h-4 rounded-full border-2 border-gray-300 shrink-0" />
              )}
              <span className={`text-sm font-semibold ${
                isComplete ? 'text-green-800' : isActive ? 'text-blue-800' : 'text-gray-400'
              }`}>
                {step.label}
                {isActive && <span className="ml-1 text-blue-500">...</span>}
              </span>
            </div>
          );
        })}
      </div>

      {/* Generated documents (shown when done) */}
      {done && (
        <div className="space-y-3 animate-fade-in">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Generated documents</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { icon: FileText, label: 'Cover Letter', pages: '2 pages', color: 'blue', id: 'gdoc-cover' },
              { icon: Globe2, label: 'Travel Itinerary', pages: '3 pages', color: 'sky', id: 'gdoc-itinerary' },
              { icon: Users, label: 'Sponsor Letter', pages: '1 page', color: 'violet', id: 'gdoc-sponsor' },
            ].map((doc) => (
              <div key={doc.id} className={`p-4 rounded-xl border bg-${doc.color}-50 border-${doc.color}-100 flex flex-col gap-2`}>
                <div className={`w-9 h-9 rounded-lg bg-white border border-${doc.color}-100 flex items-center justify-center`}>
                  <doc.icon size={17} className={`text-${doc.color}-600`} />
                </div>
                <p className="text-sm font-bold text-gray-800">{doc.label}</p>
                <p className="text-xs text-gray-400">{doc.pages} · PDF</p>
                <button
                  onClick={() => setShowPreview(true)}
                  className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline mt-1"
                >
                  <Eye size={11} />
                  Preview
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cover letter preview modal */}
      {showPreview && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setShowPreview(false)}
        >
          <div
            className="bg-white rounded-2xl border border-gray-100 shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-blue-500" />
                <span className="text-sm font-bold text-gray-800">AI Cover Letter Preview</span>
              </div>
              <button onClick={() => setShowPreview(false)} className="text-xs text-gray-400 hover:text-gray-600 font-semibold">
                Close
              </button>
            </div>
            <div className="p-5 overflow-y-auto max-h-[60vh]">
              <pre className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap font-sans">{coverLetterPreview}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center gap-3 pt-2">
        <button onClick={onBack} className="btn-secondary py-2.5 px-4 text-sm" disabled={!done}>
          <ChevronLeft size={15} />
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!done}
          className="btn-primary flex-1 justify-center py-2.5 text-sm"
        >
          {done ? (
            <>Preview & download package <ArrowRight size={15} /></>
          ) : (
            <><div className="w-4 h-4 rounded-full border-2 border-white/50 border-t-white animate-spin" /> Generating...</>
          )}
        </button>
      </div>
    </div>
  );
}